import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {alpha, CircularProgress, Grid, ThemeProvider, useTheme} from "@mui/material";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import {CheckCircleOutlined, ErrorOutline} from "@mui/icons-material";
import Button from "@mui/material/Button";
import useQueryParams from "../../../hooks/useQueryParams";
import {RootState, useAppSelector} from "../../../store";
import PaymentService from "../../../api/paymentService";

interface ICheckOrder {
    display: boolean
    error: boolean
    title?: string
    message?: string
    hint?: string
}


const BuyVouchersConfirmationDialog = () => {
    const {t} = useTranslation()
    const {params, remove} = useQueryParams()
    const order = params.get("order")
    const isLoggedIn = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.id);
    const [content, setContent] = useState<ICheckOrder>({display: false, error: false});
    const [loading, setLoading] = useState<boolean>(false);
    const theme = useTheme()

    const handleDialogClose = useCallback(() => {
        remove("order")
        setContent({display: false, error: false})
    }, [remove])

    useEffect(() => {
        if (order && isLoggedIn) {
            setLoading(true);
            PaymentService.checkOrder(order).then(res => setContent(res.data))
                .catch(() => handleDialogClose())
                .finally(() => setLoading(false));
        }
    }, [order, handleDialogClose, isLoggedIn]);

    const steps = [
        t('details'),
        t('payment'),
        t('confirmation')
    ]

    const confirmationStepContent = (
        <Box textAlign="center">
            {loading ? <CircularProgress size={80} color='primary'/> : <>
                {content.error ? <ErrorOutline color="primary" sx={{fontSize: "6rem"}}/> :
                    <CheckCircleOutlined color="primary" sx={{fontSize: "6rem"}}/>}
                <Typography variant="h5" color="primary"
                            fontWeight={600}>{content.title}</Typography>
                <Typography variant="subtitle1" color="primary">{content.message}</Typography>
                <Typography variant="body2"
                            sx={{color: alpha(theme.palette.primary.main, .6)}}>{content.hint}</Typography>
            </>}

        </Box>
    )

    const dialogContent = (
        <Box>
            <Stepper variant="outlined" activeStep={content.error ? 1 : 2} alternativeLabel sx={{mb: 4}}>
                {steps.map((label, idx) => (
                    <Step key={label}>
                        <StepLabel error={content.error && idx === 1}>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            {confirmationStepContent}
        </Box>

    )

    const dialogActions = (
        <Grid container justifyContent="flex-end" alignItems="center">
            <Button
                variant="contained"
                onClick={handleDialogClose}
            >
                {t('close')}
            </Button>
        </Grid>

    )

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CustomDialog open={content.display}
                          title={t('buy_voucher', {count: 2})}
                          maxWidth='sm'
                          actions={dialogActions}
                          handleDialogClose={handleDialogClose}
                          content={dialogContent}/>
        </ThemeProvider>
    )
}

export default BuyVouchersConfirmationDialog
